/**
 * Utility for parsing unstructured text to extract tradesperson information
 *
 * Features:
 * - Extracts phone numbers, emails, and postcodes using regex patterns
 * - Prioritizes mobile numbers (07...) when multiple phone numbers are found
 * - Detects trade type based on keyword frequency
 * - Extracts company/personal name using heuristics
 */
import { debug } from '@/lib/utils';

// We'd use a proper phone library in production
// For MVP, using a simplified UK phone regex pattern
// Updated regex to support both UK and international phone numbers
const PHONE_REGEX = /(?:(?:\+\d{1,3}|0)(?:\s*\d\s*){9,13})/g;
const UK_PHONE_REGEX = /(?:(?:\+44|0)(?:\s*\d\s*){9,10})/g;

// Email regex pattern
const EMAIL_REGEX = /[\w.-]+@[\w.-]+\.\w+/g;

// UK Postcode regex pattern (simplified)
const UK_POSTCODE_REGEX = /[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}/gi;

// Common trade type keywords to detect
const TRADE_KEYWORDS: Record<string, string[]> = {
  'plumber': ['plumb', 'plumbing', 'boiler', 'heating', 'water', 'pipe', 'tap', 'toilet', 'bathroom'],
  'electrician': ['electric', 'electrician', 'wiring', 'socket', 'light', 'power'],
  'builder': ['build', 'builder', 'construction', 'extension', 'renovation'],
  'decorator': ['decor', 'paint', 'decorator', 'wallpaper', 'interior'],
  'roofer': ['roof', 'roofer', 'gutter', 'tile', 'slate', 'leak'],
  'gardener': ['garden', 'gardener', 'landscap', 'lawn', 'tree', 'hedge', 'yard'],
  'cleaner': ['clean', 'cleaner', 'domestic', 'house clean', 'window clean'],
  'carpenter': ['carpen', 'wood', 'joiner', 'cabinet', 'furniture']
};

export interface ParsedTradeInfo {
  name: string;
  trade_type: string;
  phone: string;
  email: string;
  postcode: string;
  detectedFields: string[];
}

/**
 * Parse unstructured text to extract tradesperson information
 *
 * @param text The raw text to parse
 * @returns Object containing extracted information and detection status
 */
export function parseTradeInfo(text: string): ParsedTradeInfo {
  // Skip empty text
  if (!text || text.trim().length === 0) {
    return {
      name: '',
      trade_type: '',
      phone: '',
      email: '',
      postcode: '',
      detectedFields: []
    };
  }

  debug.log('Parsing trade info text:', text);

  const result: ParsedTradeInfo = {
    name: '',
    trade_type: '',
    phone: '',
    email: '',
    postcode: '',
    detectedFields: []
  };

  // Working copy of text that we'll modify as we extract information
  let workingText = text;

  // Extract phone number - prioritize mobile numbers (07...)
  const phoneMatches = Array.from(workingText.matchAll(PHONE_REGEX) || []);
  if (phoneMatches && phoneMatches.length > 0) {
    // Sort phone numbers to prioritize mobile numbers (starting with '07' or '+447')
    const sortedPhones = [...phoneMatches].sort((a, b) => {
      const aText = a[0].replace(/\D/g, ''); // Remove all non-digits
      const bText = b[0].replace(/\D/g, ''); // Remove all non-digits

      const aIsMobile = aText.startsWith('07') || aText.startsWith('447');
      const bIsMobile = bText.startsWith('07') || bText.startsWith('447');

      if (aIsMobile && !bIsMobile) return -1;
      if (!aIsMobile && bIsMobile) return 1;
      return 0;
    });

    // Take the first match after sorting (will be a mobile if available)
    const matchedPhone = sortedPhones[0][0];
    const rawPhone = matchedPhone.replace(/\D/g, ''); // Remove all non-digits

    // Format the phone number based on whether it already has international prefix
    let phone: string;
    if (matchedPhone.startsWith('+')) {
      // Already has international prefix, keep it as is
      phone = '+' + rawPhone;
    } else if (rawPhone.startsWith('0')) {
      // UK number without country code - convert to +44
      phone = '+44' + rawPhone.substring(1);
    } else if (rawPhone.startsWith('44')) {
      // UK number with country code but no + - add the +
      phone = '+' + rawPhone;
    } else {
      // Unknown format - keep as is with a + prefix
      phone = '+' + rawPhone;
    }

    result.phone = phone;
    result.detectedFields.push('phone');
    workingText = workingText.replace(sortedPhones[0][0], ' '); // Remove from working text
  }

  // Extract email
  const emailMatches = workingText.match(EMAIL_REGEX);
  if (emailMatches && emailMatches.length > 0) {
    result.email = emailMatches[0].toLowerCase();
    result.detectedFields.push('email');
    workingText = workingText.replace(emailMatches[0], ' '); // Remove from working text
  }

  // Extract postcode
  const postcodeMatches = workingText.match(UK_POSTCODE_REGEX);
  if (postcodeMatches && postcodeMatches.length > 0) {
    result.postcode = postcodeMatches[0].replace(/\s+/g, '').toUpperCase();
    result.detectedFields.push('postcode');
    workingText = workingText.replace(postcodeMatches[0], ' '); // Remove from working text
  }

  // Detect trade type based on keywords
  const lowercaseText = workingText.toLowerCase();
  let bestTradeMatch = '';
  let bestMatchCount = 0;
  let matchedKeyword = '';

  Object.entries(TRADE_KEYWORDS).forEach(([tradeType, keywords]) => {
    const matchCount = keywords.filter(keyword => lowercaseText.includes(keyword)).length;
    if (matchCount > bestMatchCount) {
      bestMatchCount = matchCount;
      bestTradeMatch = tradeType;

      // Find the actual keyword that matched as a standalone word (for removal later)
      for (const keyword of keywords) {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(workingText)) {
          matchedKeyword = keyword;
          break; // Use the first matching standalone keyword
        }
      }
    }
  });

  if (bestMatchCount > 0) {
    result.trade_type = bestTradeMatch;
    result.detectedFields.push('trade_type');

    // Smart removal of trade keywords from working text
    if (matchedKeyword) {
      // Check if the keyword appears as a standalone word
      const standaloneRegex = new RegExp(`\\b${matchedKeyword}\\b`, 'gi');
      const matches = workingText.match(standaloneRegex);

      if (matches) {
        // Only remove if it's at the end of the text or followed by punctuation/whitespace
        // This preserves names like "SuperPlumber Ltd" while removing "John plumber"
        const removalRegex = new RegExp(`\\s+${matchedKeyword}\\s*$|\\s+${matchedKeyword}\\s*[.,;:]|^${matchedKeyword}\\s+`, 'gi');
        const testRemoval = workingText.replace(removalRegex, ' ').trim();

        // Only apply removal if there's meaningful text left
        if (testRemoval.length > 2) {
          workingText = testRemoval;
        }
      }
    } else {
      // If we didn't find a standalone keyword, but we detected a trade type,
      // look for the full trade type name at the end of the text
      if (bestTradeMatch) {
        // Try removing the full trade name if it appears at the end
        const fullTradeRegex = new RegExp(`\\s+${bestTradeMatch}\\s*$`, 'gi');
        const testRemoval = workingText.replace(fullTradeRegex, ' ').trim();

        // Only apply removal if there's meaningful text left
        if (testRemoval.length > 2) {
          workingText = testRemoval;
        }
      }
    }
  }

  // Extract name (basic heuristic - first line or sentence that's not detected as something else)
  // Clean working text of common symbols and extra whitespace
  // Use a safer approach for emojis and special characters
  workingText = workingText
    // Replace emoji and special characters individually to avoid surrogate pair issues
    .replace(/☎/g, ' ')
    .replace(/☏/g, ' ')
    .replace(/📱/g, ' ')
    .replace(/📞/g, ' ')
    .replace(/🔸/g, ' ')
    .replace(/•/g, ' ')
    .replace(/◦/g, ' ')
    .replace(/‣/g, ' ')
    .replace(/⁃/g, ' ')
    .replace(/⌖/g, ' ')
    .replace(/🔹/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Try to extract name from the first substantial text chunk
  const textChunks = workingText.split(/[.,;:\n]/);
  for (const chunk of textChunks) {
    const cleaned = chunk.trim();
    if (cleaned.length > 2 && cleaned.length < 50) { // Reasonable name length
      result.name = cleaned;
      result.detectedFields.push('name');
      break;
    }
  }

  // If no name was found, use the first part of the text
  if (!result.name && workingText.length > 0) {
    const firstPart = workingText.substring(0, Math.min(30, workingText.length)).trim();
    if (firstPart.length > 0) {
      result.name = firstPart;
      result.detectedFields.push('name');
    }
  }

  debug.log('Parsed trade info:', result);
  return result;
}
